import { HealthParameterThreshold } from '../entities/HealthParameter';
import AlertSeverityEnum from '../enums/AlertSeverityEnum';
import formatNumber from './formatNumber';

const COMPONENT_LABEL: Record<string, string> = {
  systolic: 'sistólica',
  diastolic: 'diastólica',
};

/**
 * A janela de uma regra janelada — sono (≤ 5h por 3 noites) e perda de peso (> 5 em
 * 7 dias). Sem ela, "≤ 5" do sono leria como uma noite ruim bastando.
 */
const describeWindow = ({
  windowDays,
  occurrencesRequired,
  consecutiveHours,
}: HealthParameterThreshold): string => {
  // A taquicardia moderada (101–119) só vale em 3 horas seguidas: sem isto, a
  // enfermagem leria que um 105 avulso já alerta.
  if (consecutiveHours) {
    return ` em ${consecutiveHours} horas seguidas`;
  }

  if (!windowDays) {
    return '';
  }

  if (!occurrencesRequired || occurrencesRequired === 1) {
    return ` em ${windowDays} dias`;
  }

  if (occurrencesRequired === windowDays) {
    return ` por ${windowDays} dias seguidos`;
  }

  return ` em ${occurrencesRequired} de ${windowDays} dias`;
};

/**
 * Uma linha da régua em texto — ex.: `"120 a 139 (sistólica)"`, `"≥ 140"`.
 *
 * ⚠️ **Não colapsa em intervalo único.** A régua é um conjunto de linhas OR-adas, e
 * a frequência cardíaca tem duas faixas moderadas disjuntas (101–119 **ou** 40–49):
 * apresentá-la como "40 a 119" incluiria os 50–100 normais.
 */
export const describeThreshold = (
  threshold: HealthParameterThreshold,
): string => {
  const { comparator, value, valueSecondary, component } = threshold;

  const base = ((): string => {
    switch (comparator) {
      case 'between':
        return `${formatNumber(value)} a ${formatNumber(
          valueSecondary ?? value,
        )}`;
      case 'gte':
        return `≥ ${formatNumber(value)}`;
      case 'gt':
        return `> ${formatNumber(value)}`;
      case 'lte':
        return `≤ ${formatNumber(value)}`;
      default:
        return `< ${formatNumber(value)}`;
    }
  })();

  const withComponent = component
    ? `${base} (${COMPONENT_LABEL[component]})`
    : base;

  return `${withComponent}${describeWindow(threshold)}`;
};

/**
 * A régua **que o motor aplica hoje** para (parâmetro, gravidade), em texto — a
 * linha "por que este alerta disparou" da tabela de Alertas.
 *
 * ⚠️ **Só a régua geral e ativa.** As linhas de gestante chegam inativas (o back não
 * sabe quem é gestante), e mostrá-las na linha do alerta diria que elas classificaram
 * aquela leitura, o que é falso.
 *
 * Devolve `''` quando o parâmetro não tem régua (ex.: silêncio de dispositivo).
 */
export const rulerFor = (
  thresholds: HealthParameterThreshold[],
  parameter: string,
  severity: AlertSeverityEnum,
): string =>
  thresholds
    .filter(
      (threshold) =>
        threshold.parameterSet === 'alerta' &&
        (threshold.careLine ?? null) === null &&
        threshold.active &&
        threshold.parameter === parameter &&
        threshold.severity === severity,
    )
    .map(describeThreshold)
    .join(' ou ');
